import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { from, rpc, unwrap, useAction, useOrg } from "@vixio/data";
import {
  Button, Chart, DataTable, EmptyState, KpiRow, KpiTile, PageHeader, Panel, Section, formatDate, formatInt, formatMoney, formatMoneyCompact, formatPct,
  todayIso,
} from "@vixio/ui";
import type { DueFollowup, Weekly } from "./api";

export function useWeekly(orgId: string) {
  return useQuery({
    queryKey: ["sales", "v_weekly", orgId],
    queryFn: () => unwrap<Weekly[]>(from("sales", "v_weekly").select("*").eq("org_id", orgId).order("week_start", { ascending: false }).limit(12)),
  });
}

export function useDueFollowups(orgId: string) {
  return useQuery({
    queryKey: ["sales", "due_followups", orgId],
    queryFn: async () => ((await rpc("sales", "due_followups", { p_org: orgId, p_date: null })) ?? []) as DueFollowup[],
  });
}

function weekLabel(w: Weekly): string {
  return `${w.iso_week.replace(/^\d{4}-W/, "sem ")} (${formatDate(w.week_start).slice(0, 5)})`;
}

export function WeeklyPage() {
  const org = useOrg();
  const weekly = useWeekly(org.id);
  const due = useDueFollowups(org.id);
  const rows = weekly.data ?? [];
  const current = rows[0];
  const chrono = [...rows].reverse();

  const complete = useAction((a: { id: string; outcome: "enviado" | "respondeu" }) => rpc("sales", "complete_followup", { p_followup: a.id, p_outcome: a.outcome }), {
    invalidate: [["sales", "due_followups"]],
    success: "Acompanhamento concluído",
  });

  return (
    <div className="vx-content">
      <PageHeader title="Painel semanal" description="Os números do PM3 §15, semana ISO no horário da Bahia: conversas, propostas, valor em negociação, aceites e conversão." />
      {current ? (
        <KpiRow>
          <KpiTile label="Conversas qualificadas" value={formatInt(current.conversas_qualificadas)} foot={weekLabel(current)} />
          <KpiTile label="Propostas enviadas" value={formatInt(current.propostas_enviadas)} />
          <KpiTile label="Em negociação" value={formatMoney(current.valor_em_negociacao_cents)} foot={`proposto ${formatMoney(current.valor_proposto_cents)}`} />
          <KpiTile label="Aceites" value={formatInt(current.propostas_aceitas)} foot={`contratado ${formatMoney(current.receita_contratada_cents)}`} tone={current.propostas_aceitas > 0 ? "ok" : undefined} />
          <KpiTile label="Conversão" value={current.taxa_conversao === null ? "-" : formatPct(Number(current.taxa_conversao) * 100)} foot="aceitas ÷ enviadas na semana" />
        </KpiRow>
      ) : (
        <Panel>
          <EmptyState title="Sem movimento ainda">Registre conversas de 20 minutos e envie propostas para os números aparecerem.</EmptyState>
        </Panel>
      )}
      {chrono.length > 0 ? (
        <div className="vx-grid vx-grid--2 vx-section">
          <Panel title="Conversas e propostas por semana">
            <Chart
              type="bar"
              title="Conversas qualificadas e propostas enviadas por semana"
              labels={chrono.map(weekLabel)}
              series={[
                { name: "Conversas", values: chrono.map((w) => w.conversas_qualificadas) },
                { name: "Propostas", values: chrono.map((w) => w.propostas_enviadas) },
                { name: "Aceites", values: chrono.map((w) => w.propostas_aceitas) },
              ]}
              format={(v) => formatInt(v)}
            />
          </Panel>
          <Panel title="Receita contratada por semana">
            <Chart
              type="bar"
              title="Receita contratada por semana"
              labels={chrono.map(weekLabel)}
              series={[{ name: "Contratada", values: chrono.map((w) => w.receita_contratada_cents) }]}
              format={(v) => formatMoney(v)}
              formatAxis={(v) => formatMoneyCompact(v)}
            />
          </Panel>
        </div>
      ) : null}
      <Section title="Acompanhamentos vencidos" id="followups">
        <DataTable<DueFollowup>
          caption="Acompanhamentos de propostas vencidos"
          data={due.data ?? []}
          loading={due.isLoading}
          rowKey={(f) => f.followup_id}
          empty="Nenhum acompanhamento vencido. A cadência é 7 e 21 dias depois do envio."
          columns={[
            { key: "due_on", header: "Venceu", kind: "date" },
            { key: "proposal_number", header: "Proposta", cell: (f) => <Link to={`/comercial/propostas/${f.proposal_id}`} className="mono">{f.proposal_number}</Link> },
            { key: "who", header: "Para", cell: (f) => f.person_name ?? f.account_name ?? "-", csv: (f) => f.person_name ?? f.account_name ?? "" },
            { key: "step", header: "Passo", kind: "number" },
            { key: "message", header: "Mensagem sugerida", cell: (f) => <span className="text-2" style={{ whiteSpace: "pre-wrap", fontSize: "var(--fs-12)" }}>{f.message}</span> },
            {
              key: "actions",
              header: <span className="sr-only">Ações</span>,
              align: "right",
              csv: false,
              cell: (f) => (
                <span className="vx-row" style={{ justifyContent: "flex-end", flexWrap: "nowrap" }}>
                  <Button size="sm" onClick={() => complete.mutate({ id: f.followup_id, outcome: "enviado" })}>
                    Enviei
                  </Button>
                  <Button size="sm" onClick={() => complete.mutate({ id: f.followup_id, outcome: "respondeu" })}>
                    Respondeu
                  </Button>
                </span>
              ),
            },
          ]}
        />
      </Section>
      <Section title="Semanas" id="semanas">
        <DataTable<Weekly>
          caption="Histórico semanal"
          data={rows}
          loading={weekly.isLoading}
          rowKey={(w) => w.week_start}
          exportName="painel-semanal"
          columns={[
            { key: "iso_week", header: "Semana", cell: (w) => weekLabel(w), csv: (w) => w.iso_week },
            { key: "conversas_qualificadas", header: "Conversas", kind: "number" },
            { key: "propostas_enviadas", header: "Enviadas", kind: "number" },
            { key: "valor_proposto_cents", header: "Proposto", kind: "money" },
            { key: "valor_em_negociacao_cents", header: "Em negociação", kind: "money" },
            { key: "propostas_aceitas", header: "Aceites", kind: "number" },
            { key: "receita_contratada_cents", header: "Contratado", kind: "money" },
            { key: "taxa_conversao", header: "Conversão", align: "right", cell: (w) => (w.taxa_conversao === null ? "-" : formatPct(Number(w.taxa_conversao) * 100)) },
          ]}
        />
      </Section>
      <p className="muted" style={{ marginTop: 16, fontSize: "var(--fs-12)" }}>
        Atualizado em {formatDate(todayIso())}.
      </p>
    </div>
  );
}
